<script setup lang="ts">
  import { provide, ref } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { type PhysicalLocation } from '@/entities/Document';
  import AddIcon from '@/shared/assets/icons/AddIcon.svg';
  import { PHYSICAL_LOCATION_QUANTITY_OPTIONS, SHOW_ALL_ITEMS_QUANTITY } from '@/shared/config';
  import { toastError, useCustomToast } from '@/shared/lib';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import {
    ColoredButton,
    ConfirmDeleteModal,
    PaginateOrScroll,
    SearchBar,
    SearchToolbar,
    SelectInput,
  } from '@/shared/ui';

  import { documentManagementMessages } from '../lib/messages';
  import { useCreatePhysicalLocations } from '../lib/useCreatePhysicalLocations';
  import { useDeletePhysicalLocation } from '../lib/useDeletePhysicalLocation';
  import { usePhysicalLocationsList } from '../lib/usePhysicalLocationsList';
  import { useUpdatePhysicalLocation } from '../lib/useUpdatePhysicalLocation';
  import CreatePhysicalLocationsModal from './CreatePhysicalLocationsModal.vue';
  import EditPhysicalLocationModal from './EditPhysicalLocationModal.vue';
  import PhysicalLocationsTable from './PhysicalLocationsTable.vue';

  const {
    items,
    allItems,
    metadata,
    loading,
    loadingMore,
    hasMoreItems,
    currentPage,
    searchTerm,
    selectedQuantity,
    currentSort,
    currentOrder,
    orderOptions,
    handleSearch,
    handleSort,
    fetchPage,
    insertCreatedItems,
    refreshList,
  } = usePhysicalLocationsList();

  const { createPhysicalLocations, isCreating } = useCreatePhysicalLocations();
  const { deletePhysicalLocation, isDeleting } = useDeletePhysicalLocation();
  const { updatePhysicalLocation, isUpdating } = useUpdatePhysicalLocation();
  const toast = useCustomToast();
  const selectedLocation = ref<PhysicalLocation | null>(null);
  const deletingLocation = ref<PhysicalLocation | null>(null);
  const editInitialName = ref('');
  const createNameErrors = ref<string[]>([]);
  const editNameError = ref('');

  const syncUpdatedLocation = (updatedLocation: PhysicalLocation) => {
    for (const collection of [items.value, allItems.value]) {
      const location = collection.find((item) => item.id === updatedLocation.id);

      if (location) {
        Object.assign(location, updatedLocation);
      }
    }

    if (selectedLocation.value?.id === updatedLocation.id) {
      Object.assign(selectedLocation.value, updatedLocation);
    }
  };

  const findLocationById = (id: number) =>
    items.value.find((item) => item.id === id) ??
    allItems.value.find((item) => item.id === id) ??
    null;

  const createPhysicalLocationsModal = useModal({
    component: CreatePhysicalLocationsModal,
    attrs: {
      title: documentManagementMessages.physicalLocations.createModalTitle,
      get nameErrors() {
        return createNameErrors.value;
      },
      get isSaving() {
        return isCreating.value;
      },
      onClose() {
        createNameErrors.value = [];
        createPhysicalLocationsModal.close();
      },
      async onSave(data: { names: string[] }) {
        const result = await createPhysicalLocations(data.names);

        if (result.success) {
          insertCreatedItems(result.createdLocations);
          createPhysicalLocationsModal.close();
          createNameErrors.value = [];
          return;
        }

        if (result.nameErrors) {
          createNameErrors.value = result.nameErrors;
        }
      },
    },
  });

  const editPhysicalLocationsModal = useModal({
    component: EditPhysicalLocationModal,
    attrs: {
      title: 'Editar local',
      get initialName() {
        return editInitialName.value;
      },
      get nameError() {
        return editNameError.value;
      },
      get isSaving() {
        return isUpdating.value;
      },
      onClose() {
        selectedLocation.value = null;
        editNameError.value = '';
        editPhysicalLocationsModal.close();
      },
      async onSave(data: { name: string }) {
        if (!selectedLocation.value) {
          return;
        }

        const result = await updatePhysicalLocation(selectedLocation.value.id, [data.name]);

        if (result.success) {
          if (result.updatedLocation) {
            syncUpdatedLocation(result.updatedLocation);
          }

          insertCreatedItems(result.createdLocations ?? []);
          selectedLocation.value = null;
          editPhysicalLocationsModal.close();
          editNameError.value = '';
          return;
        }

        if (result.nameErrors) {
          editNameError.value = result.nameErrors.find(Boolean) ?? '';
        }
      },
    },
  });

  const deletePhysicalLocationsModal = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      get title() {
        return documentManagementMessages.physicalLocations.deleteDialogTitle;
      },
      get description() {
        return documentManagementMessages.physicalLocations.deleteDialogDescription;
      },
      get isDeleting() {
        return isDeleting.value;
      },
      onClose() {
        deletingLocation.value = null;
        deletePhysicalLocationsModal.close();
      },
      async onConfirm() {
        if (!deletingLocation.value || isDeleting.value) {
          return;
        }

        const result = await deletePhysicalLocation(deletingLocation.value.id);

        if (!result.success) {
          return;
        }

        deletingLocation.value = null;
        deletePhysicalLocationsModal.close();

        const shouldGoToPreviousPage =
          selectedQuantity.value !== SHOW_ALL_ITEMS_QUANTITY &&
          items.value.length === 1 &&
          currentPage.value > 1;

        if (shouldGoToPreviousPage) {
          await fetchPage(currentPage.value - 1);
          return;
        }

        await refreshList();
      },
    },
  });

  provide(SortOrderInjectionKey, {
    sort: currentSort,
    order: currentOrder,
  });

  const handleAddLocation = () => {
    createNameErrors.value = [];
    createPhysicalLocationsModal.open();
  };

  const handleEditLocation = (id: number) => {
    const location = findLocationById(id);

    if (!location) {
      toastError(toast, documentManagementMessages.physicalLocations.selectedLocationNotFoundToast);
      return;
    }

    selectedLocation.value = location;
    editInitialName.value = location.name;
    editNameError.value = '';
    editPhysicalLocationsModal.open();
  };

  const handleDeleteLocation = (id: number) => {
    const location = findLocationById(id);

    if (!location) {
      toastError(toast, documentManagementMessages.physicalLocations.selectedLocationNotFoundToast);
      return;
    }

    deletingLocation.value = location;
    deletePhysicalLocationsModal.open();
  };
</script>

<template>
  <section
    data-testid="document-management-locations-page"
    class="space-y-6"
  >
    <div class="flex flex-col gap-4 w-full">
      <ColoredButton
        class="self-start"
        data-testid="document-management-locations-add-button"
        :text="documentManagementMessages.physicalLocations.createButtonText"
        :icon="AddIcon"
        :icon-alt="documentManagementMessages.physicalLocations.createButtonIconAlt"
        @click="handleAddLocation"
      />

      <SearchToolbar>
        <template #search>
          <div
            data-testid="document-management-locations-search"
            class="w-full"
          >
            <SearchBar
              :initial-search-term="searchTerm"
              @search="handleSearch"
            />
          </div>
        </template>
        <template #quantity>
          <SelectInput
            v-model="selectedQuantity"
            data-testid="document-management-locations-items-per-page"
            :options="PHYSICAL_LOCATION_QUANTITY_OPTIONS"
            class="cursor-pointer"
          />
        </template>
      </SearchToolbar>
    </div>

    <PaginateOrScroll
      :items="items"
      :all-items="allItems"
      :has-more-items="hasMoreItems"
      :loading-more="loadingMore"
      :metadata="metadata"
      :selected-quantity="selectedQuantity"
      entity-name="local"
      @page-changed="fetchPage"
    >
      <template #table="{ items: tableItems }">
        <PhysicalLocationsTable
          data-testid="document-management-locations-card"
          :items="tableItems"
          :loading="loading"
          :order-options="orderOptions"
          @update:sort="handleSort"
          @edit="handleEditLocation"
          @delete="handleDeleteLocation"
        />
      </template>
    </PaginateOrScroll>
  </section>
</template>
